import * as React from "react";
import Container from "@mui/material/Container";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Icon from "@mui/material/Icon";
import TranslateIcon from "@mui/icons-material/Translate";
import DescriptionIcon from "@mui/icons-material/Description";
import MovieIcon from "@mui/icons-material/Movie";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import { useQuery } from "react-query";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Breadcrumbs } from "../../components/Breadcrumbs/Breadcrumbs";
import { TextTranslationTab } from "./TextTranslationTab/TextTranslationTab";
import { FileTranslationTab } from "./FileTranslationTab/FileTranslationTab";
import { MachineTranslationService } from "../../gen/clients/llts";
import { LoadingPage } from "../../components/LoadingPage/LoadingPage";
import { ErrorPage } from "../../components/ErrorPage/ErrorPage";
import { MediaTranslationTab } from "./MediaTranslationTab/MediaTranslationTab";
import { AiToolboxTab } from "./AiToolboxTab/AiToolboxTab";
import { useNavigation } from "../../hooks/useNavigation";
import { ROUTES } from "../../utils/navigationUtils";
import Typography from "@mui/material/Typography";

export enum MachineTranslationTabId {
  textTranslation = "textTranslation",
  fileTranslation = "fileTranslation",
  mediaTranslation = "mediaTranslation",
  aiToolbox = "aiToolbox"
}

interface Props {
  selectedTabId?: MachineTranslationTabId;
}

const MachineTranslationPage: React.FC<Props> = ({ selectedTabId }) => {
  const { t } = useTranslation();
  const { navigateTo } = useNavigation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

  const {
    data: mtSettings,
    isLoading: areMtSettingsLoading,
    error: mtSettingsError
  } = useQuery(["getMtSettings"], {
    queryFn: MachineTranslationService.getMachineTranslationSettings
  });

  const breadcrumbs = React.useMemo(
    () => [
      {
        label: t("crumbText.home"),
        path: ROUTES.HOME,
        active: false
      },
      {
        label: t("machineTranslation.title"),
        path: ROUTES.MACHINE_TRANSLATION,
        active: true
      }
    ],
    [t]
  );

  if (areMtSettingsLoading) {
    return <LoadingPage />;
  }

  if (mtSettingsError) {
    return <ErrorPage apiError={mtSettingsError} />;
  }

  const tabId =
    selectedTabId ||
    (mtSettings?.disableTextTranslation
      ? mtSettings?.disableFileTranslation
        ? MachineTranslationTabId.mediaTranslation
        : MachineTranslationTabId.fileTranslation
      : MachineTranslationTabId.textTranslation);

  return (
    <Container maxWidth="xl" sx={{ my: 4 }}>
      <Breadcrumbs breadcrumbs={breadcrumbs} />

      <Box>
        <Tabs
          variant={isSmallScreen ? "fullWidth" : "standard"}
          value={tabId}
          onChange={(event, value) => navigateTo(`${ROUTES.MACHINE_TRANSLATION}/${value}`)}
        >
          {!mtSettings?.disableTextTranslation && (
            <Tab
              value={MachineTranslationTabId.textTranslation}
              label={t("machineTranslation.tabs.textTranslation")}
              icon={
                <Icon>
                  <TranslateIcon />
                </Icon>
              }
              iconPosition="start"
              sx={{ minHeight: 0 }}
              wrapped={isSmallScreen}
            />
          )}
          {!mtSettings?.disableFileTranslation && (
            <Tab
              value={MachineTranslationTabId.fileTranslation}
              label={t("machineTranslation.tabs.fileTranslation")}
              icon={
                <Icon>
                  <DescriptionIcon />
                </Icon>
              }
              iconPosition="start"
              sx={{ minHeight: 0 }}
              wrapped={isSmallScreen}
            />
          )}
          {mtSettings?.disableMediaTranslation === false && (
            <Tab
              value={MachineTranslationTabId.mediaTranslation}
              label={t("machineTranslation.tabs.mediaTranslation")}
              icon={
                <Icon>
                  <MovieIcon />
                </Icon>
              }
              iconPosition="start"
              sx={{ minHeight: 0 }}
              wrapped={isSmallScreen}
            />
          )}
          {mtSettings?.enableAiToolbox === true && (
            <Tab
              value={MachineTranslationTabId.aiToolbox}
              label={t("machineTranslation.tabs.aiToolbox")}
              icon={
                <Icon>
                  <AutoFixHighIcon />
                </Icon>
              }
              iconPosition="start"
              sx={{ minHeight: 0, marginLeft: "auto" }}
              wrapped={isSmallScreen}
            />
          )}
        </Tabs>
      </Box>
      {tabId === MachineTranslationTabId.textTranslation && !mtSettings?.disableTextTranslation && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="h5">{t("machineTranslation.tabs.textTranslation")}</Typography>
          <TextTranslationTab />
        </Box>
      )}
      {tabId === MachineTranslationTabId.fileTranslation && !mtSettings?.disableFileTranslation && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="h5">{t("machineTranslation.tabs.fileTranslation")}</Typography>
          <FileTranslationTab />
        </Box>
      )}
      {tabId === MachineTranslationTabId.mediaTranslation && !mtSettings?.disableMediaTranslation && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="h5">{t("machineTranslation.tabs.mediaTranslation")}</Typography>
          <MediaTranslationTab />
        </Box>
      )}
      {tabId === MachineTranslationTabId.aiToolbox && mtSettings?.enableAiPostEditing && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="h5">{t("machineTranslation.tabs.aiToolbox")}</Typography>
          <AiToolboxTab />
        </Box>
      )}
    </Container>
  );
};

export { MachineTranslationPage };
